// Spa liveness — deciding when a connected spa has gone offline.
//
// A spa that loses power or its network can't announce it. The AWS presence
// `disconnected` event is the only thing that could, and in practice it never
// reaches the portal, so a spa once marked connected stayed that way. Silence is
// the signal instead: when a connected spa has published nothing for a while, ask
// it to report its connection status, and treat no reply as offline.

/** How long a connected spa can be silent before we ask whether it's still there. */
export const SPA_SILENCE_MS = 60_000;

/** How long the spa has to answer that request before it's shown as offline. */
export const SPA_PROBE_TIMEOUT_MS = 15_000;

/** How often the watchdog re-checks while the spa is connected. */
export const SPA_WATCHDOG_TICK_MS = 5_000;

// Topics the spa publishes on. `command` is left out: anything there is the portal's
// own publish coming back to it.
const SPA_TOPICS = new Set([
    "live",
    "sett",
    "const",
    "error",
    "status",
    "state",
    "update-status",
    "connection-status",
]);

/**
 * Whether a message shows the spa is online right now.
 *
 * - Retained messages don't: the broker replays them on every subscribe, whether
 *   or not the spa is still there.
 * - On `connection-status`, only a "connected" report does. The portal's own
 *   `report_connection_status` request arrives on the same topic.
 */
export function isSpaActivity(topicName: string, payload: string, retained: boolean): boolean {
    if (retained || !SPA_TOPICS.has(topicName)) return false;

    if (topicName === "connection-status") {
        try {
            return JSON.parse(payload)?.connection_status === "connected";
        } catch {
            return false;
        }
    }

    return true;
}

export type LivenessAction = "wait" | "probe" | "offline";

/**
 * What the watchdog for a connected spa should do now.
 *
 * @param lastSpaActivityAt When the spa last published (see isSpaActivity).
 * @param probeSentAt When the still-unanswered status request went out, or null if
 *   none is pending. The caller clears it as soon as the spa publishes anything.
 */
export function nextLivenessAction(
    now: number,
    lastSpaActivityAt: number,
    probeSentAt: number | null
): LivenessAction {
    if (probeSentAt !== null) {
        return now - probeSentAt >= SPA_PROBE_TIMEOUT_MS ? "offline" : "wait";
    }
    return now - lastSpaActivityAt >= SPA_SILENCE_MS ? "probe" : "wait";
}
